import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, X, Edit } from "lucide-react";

const KINDS = ["deliverable", "payment", "review", "renewal_notice", "board_approval", "insurance_check", "custom"];
const CONTRACT_TYPES = [
  "New-Vendor","New-Provider","Amendment","Renewal-Extension","Term-Change","Value-Change","BAA","MOU","IGA","Grant","SOW/PO","Other"
];

export default function MilestoneTemplatesAdmin() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", contract_type: "New-Vendor", active: true, items: [{ label: "", kind: "custom", offset_days: 30, notes: "" }] });

  const load = () => api.get("/milestone-templates").then(r => setRows(r.data || [])).catch(() => toast.error("Failed"));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", contract_type: "New-Vendor", active: true, items: [{ label: "", kind: "custom", offset_days: 30, notes: "" }] });
    setModal("create");
  };
  const openEdit = (t) => { setForm({ name: t.name, contract_type: t.contract_type, active: t.active, items: t.items || [] }); setModal({ edit: t }); };
  const save = async () => {
    if (!form.name.trim() || !form.items.length) return toast.error("Name + at least one item required");
    const body = { ...form, items: form.items.filter(i => i.label.trim()) };
    if (!body.items.length) return toast.error("At least one item with a label");
    try {
      if (modal === "create") await api.post("/milestone-templates", body);
      else await api.patch(`/milestone-templates/${modal.edit.id}`, body);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/milestone-templates/${t.id}`); load(); } catch { toast.error("Failed"); }
  };
  const updateItem = (i, patch) => setForm(f => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { label: "", kind: "custom", offset_days: 30, notes: "" }] }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  return (
    <div className="p-6" data-testid="ms-templates-page">
      <div className="flex items-center gap-3 mb-4">
        <ClipboardList className="w-5 h-5 text-[#005289]"/>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Administration · Milestone Templates</div>
          <h1 className="text-2xl font-bold text-slate-900">Milestone Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Reusable milestone sets that auto-attach to new contracts by contract type on submit.</p>
        </div>
        <button onClick={openCreate} data-testid="ms-tpl-create"
                className="ml-auto h-9 px-4 rounded text-xs font-bold text-white bg-[#005289] flex items-center gap-1"><Plus className="w-3 h-3"/>New Template</button>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="hcd-table text-xs w-full">
          <thead><tr><th>Name</th><th>Contract Type</th><th>Items</th><th>Active</th><th className="text-right">Actions</th></tr></thead>
          <tbody data-testid="ms-tpl-rows">
            {rows.length === 0 ? <tr><td colSpan={5} className="p-6 text-center text-slate-400">No templates yet.</td></tr> :
              rows.map(t => (
                <tr key={t.id} data-testid={`ms-tpl-row-${t.id}`}>
                  <td className="font-semibold">{t.name}</td>
                  <td><span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold">{t.contract_type}</span></td>
                  <td className="text-slate-600">{(t.items || []).length}</td>
                  <td>{t.active ? <span className="text-emerald-600 font-semibold">Yes</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="text-right">
                    <button onClick={() => openEdit(t)} className="h-7 px-2 text-[10px] border rounded mr-1 inline-flex items-center gap-1"><Edit className="w-3 h-3"/>Edit</button>
                    <button onClick={() => del(t)} className="h-7 px-2 text-[10px] border rounded text-red-600 inline-flex items-center gap-1"><Trash2 className="w-3 h-3"/></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-md w-[720px] max-w-[95vw] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ms-tpl-modal">
            <div className="px-5 py-3 border-b flex items-center gap-2 sticky top-0 bg-white z-10">
              <div className="font-bold text-sm">{modal === "create" ? "New Milestone Template" : `Edit ${modal.edit?.name}`}</div>
              <button onClick={() => setModal(null)} className="ml-auto"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-5 space-y-3 text-xs">
              <div className="grid grid-cols-3 gap-3">
                <label className="col-span-2"><span className="font-bold block mb-1">Template name</span>
                  <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ms-tpl-name" className="w-full h-9 px-3 border border-slate-200 rounded"/>
                </label>
                <label><span className="font-bold block mb-1">Contract type</span>
                  <select value={form.contract_type} onChange={e => setForm({...form, contract_type: e.target.value})} data-testid="ms-tpl-type" className="w-full h-9 px-2 border border-slate-200 rounded">
                    {CONTRACT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </label>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/><span>Active</span></label>

              <div className="border border-slate-200 rounded">
                <div className="px-3 py-2 border-b bg-slate-50 flex items-center gap-2">
                  <span className="font-bold uppercase text-[10px] text-slate-500">Milestones ({form.items.length})</span>
                  <button onClick={addItem} data-testid="ms-tpl-add-item" className="ml-auto text-[10px] px-2 py-1 border rounded hover:bg-slate-100 flex items-center gap-1"><Plus className="w-3 h-3"/>Add item</button>
                </div>
                <div className="divide-y">
                  {form.items.map((it, i) => (
                    <div key={i} className="p-3 grid grid-cols-6 gap-2 items-center">
                      <input value={it.label} onChange={e => updateItem(i, { label: e.target.value })} placeholder="Label" data-testid={`ms-tpl-item-label-${i}`} className="col-span-2 h-8 px-2 border border-slate-200 rounded"/>
                      <select value={it.kind} onChange={e => updateItem(i, { kind: e.target.value })} className="h-8 px-2 border border-slate-200 rounded">
                        {KINDS.map(k => <option key={k}>{k}</option>)}
                      </select>
                      <input type="number" value={it.offset_days} onChange={e => updateItem(i, { offset_days: +e.target.value || 0 })} placeholder="Days after submit" className="h-8 px-2 border border-slate-200 rounded" title="Days after contract creation"/>
                      <input value={it.notes || ""} onChange={e => updateItem(i, { notes: e.target.value })} placeholder="Notes" className="h-8 px-2 border border-slate-200 rounded"/>
                      <button onClick={() => rmItem(i)} className="text-red-600"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setModal(null)} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
              <button onClick={save} data-testid="ms-tpl-save" className="h-9 px-4 rounded bg-[#005289] text-white text-xs font-semibold">Save Template</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
